import * as React from 'react'
import { ReactElement } from 'react'
import { useFormikContext } from 'formik'
import { Gender } from '../../../../config/contactConfig'
import { NaturalPersonMutationDto } from '../../../config/partyMutationConfig'
import FieldRow from './FieldRow'
import useSalutationAutoFill from './useSalutationAutoFill'
import FormikInputField from '../../../../components/Formik/FormikInputField'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import FormikField from '../../../../components/Formik/FormikField'
import EnumCombobox, { EnumComboboxProps } from '../../../../components/Combobox/EnumCombobox'
import LocalDatePicker, { LocalDatePickerProps } from '../../../../components/DatePicker/LocalDatePicker'
import useViewport, { Viewport } from '../../../../hooks/useViewport'

/**
 * Name & person details of a natural person (HAM-3911). Keeps the automatic salutation/attention-of
 * fill (until manually edited) via {@link useSalutationAutoFill}.
 */
export default function PersonNameSection(): ReactElement {
    const { values, setFieldValue } = useFormikContext<NaturalPersonMutationDto>()
    const { onSalutationEdited, onAttentionOfEdited } = useSalutationAutoFill<NaturalPersonMutationDto>()
    const { isViewport } = useViewport()

    // On narrow panes (<= OutlookNew) the four-column name row cramps its labels, so drop the last
    // name onto its own full-width line and keep given/initials/insertion on the first line.
    const stackLastName = isViewport('<=', Viewport.OutlookNew)

    // Nested (dotted) field paths aren't keyof the DTO, so these use the untyped variant like the legacy form did.
    const given = <FormikInputField<any> labelKey="party.edit.person.given" fieldName="name.given" componentProps={{ autoFocus: true }} />
    const initials = <FormikInputField<any> labelKey="party.edit.person.initials" fieldName="name.initials" />
    const insertion = <FormikInputField<any> labelKey="party.edit.person.insertion" fieldName="name.insertion" />
    const last = <FormikInputField<any> required labelKey="party.edit.person.last" fieldName="name.last" />

    return (
        <>
            {stackLastName ? (
                <>
                    <FieldRow template="1fr 1fr 1fr">
                        {given}
                        {initials}
                        {insertion}
                    </FieldRow>
                    <FieldRow>{last}</FieldRow>
                </>
            ) : (
                <FieldRow template="2fr 1fr 1fr 2fr">
                    {given}
                    {initials}
                    {insertion}
                    {last}
                </FieldRow>
            )}
            <FieldRow>
                <FormikInputField<any> labelKey="party.edit.person.first" fieldName="name.first" />
                <FormikInputField<NaturalPersonMutationDto>
                    required={values.crmRelation}
                    labelKey="party.edit.person.shortname"
                    fieldName="shortName"
                />
            </FieldRow>
            <FieldRow template="1fr 1fr">
                <FormikComboboxField<NaturalPersonMutationDto, EnumComboboxProps<Gender>>
                    required
                    labelKey="base.gender.label"
                    fieldName="gender"
                    component={EnumCombobox}
                    componentProps={{ entities: Object.values(Gender), labelKeyPrefix: 'base.gender', clearable: false }}
                />
                <FormikField<LocalDatePickerProps, NaturalPersonMutationDto>
                    labelKey="party.edit.other.dateofbirth"
                    fieldName="dateOfBirth"
                    component={LocalDatePicker}
                    componentProps={{ isoValue: values.dateOfBirth, onSelectDate: date => setFieldValue('dateOfBirth', date) }}
                />
            </FieldRow>
            <FieldRow>
                <FormikInputField<NaturalPersonMutationDto>
                    labelKey="party.edit.person.salutation"
                    fieldName="salutation"
                    componentProps={{ onBlur: onSalutationEdited, onKeyUp: onSalutationEdited }}
                />
                <FormikInputField<NaturalPersonMutationDto>
                    labelKey="party.edit.person.attentionOf"
                    fieldName="attentionOf"
                    componentProps={{ onBlur: onAttentionOfEdited, onKeyUp: onAttentionOfEdited }}
                />
            </FieldRow>
        </>
    )
}
